import { Link } from "react-router-dom";
import Seo from "@/components/base/Seo";
import FeedbackForm from "@/pages/about/components/FeedbackForm";
import { categories } from "@/mocks/categories";

const services = [
  {
    icon: "ri-newspaper-line",
    title: "及时资讯聚合",
    desc: "覆盖音乐、影视、时尚与潮流文化四大板块，持续更新行业动态、作品评论与现场观察。",
  },
  {
    icon: "ri-bookmark-3-line",
    title: "深度专题策划",
    desc: "围绕一个主题整合多篇内容与编辑观点，把零散信息串成有脉络的阅读线索。",
  },
  {
    icon: "ri-filter-3-line",
    title: "分类与筛选",
    desc: "按分类、标签、发布时间与热度多维筛选排序，快速定位你真正想读的内容。",
  },
  {
    icon: "ri-search-line",
    title: "搜索与收藏",
    desc: "支持关键词即时匹配（演示），并可收藏感兴趣的文章，构建属于你的阅读清单。",
  },
];

const milestones = [
  { year: "起步", title: "从一个资讯角落开始", desc: "最初只是几位编辑分享喜欢的音乐与电影，慢慢地，越来越多的读者加入进来。" },
  { year: "成长", title: "四大板块成型", desc: "内容从音乐与影视扩展到时尚与潮流文化，形成如今的内容结构。" },
  { year: "现在", title: "坚持做有态度的内容", desc: "我们相信娱乐资讯也可以被认真对待，好的作品值得被认真讲述。" },
];

export default function AboutPage() {
  return (
    <>
      <Seo
        title="关于我们 - 噜噜社"
        description="了解噜噜社的品牌故事、服务范围与联系方式。噜噜社是一个综合娱乐资讯与潮流文化平台，覆盖音乐、影视、时尚与潮流文化。"
        keywords="关于噜噜社,娱乐资讯平台,潮流文化,媒体介绍,联系方式"
      />

      <section className="relative h-64 w-full overflow-hidden md:h-80">
        <img
          src="https://readdy.ai/api/search-image?query=Stylized%20creative%20studio%20desk%20overhead%20view%20with%20notebooks%20camera%20vinyl%20record%20and%20coffee%2C%20warm%20orange%20and%20cream%20palette%2C%20soft%20natural%20light%2C%20grainy%20texture%2C%20minimal%20editorial%20composition&width=1600&height=700&seq=lulu-about-banner&orientation=landscape"
          alt="噜噜社品牌视觉"
          title="关于噜噜社 - 综合娱乐资讯与潮流文化平台"
          className="h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-secondary-950/85 via-secondary-950/60 to-secondary-950/30" />
        <div className="absolute inset-0 mx-auto flex w-full max-w-[1280px] flex-col justify-center px-4 md:px-6">
          <span className="text-xs font-semibold uppercase tracking-[0.3em] text-primary-300">ABOUT LULU CLUB</span>
          <h1 className="mt-2 text-3xl font-extrabold text-background-50 md:text-4xl">关于噜噜社</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-background-100/90 md:text-base">
            一个认真对待娱乐与潮流的资讯平台，用内容连接热爱。
          </p>
        </div>
      </section>

      <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-6">
        <nav className="flex flex-wrap items-center gap-2 text-sm text-foreground-500">
          <Link to="/" className="transition-colors hover:text-primary-600">
            首页
          </Link>
          <i className="ri-arrow-right-s-line" />
          <span className="font-medium text-foreground-900">关于我们</span>
        </nav>
      </div>

      {/* 品牌故事 */}
      <section className="mx-auto grid w-full max-w-[1280px] grid-cols-1 gap-10 px-4 pb-4 md:px-6 lg:grid-cols-2">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-800">
            <i className="ri-heart-3-line" />
            品牌故事
          </span>
          <h2 className="mt-4 text-2xl font-extrabold leading-snug text-foreground-950 md:text-3xl">
            我们想做的，是把热闹背后的门道讲清楚
          </h2>
          <div className="mt-4 flex flex-col gap-4 text-sm leading-loose text-foreground-600 md:text-base">
            <p>
              噜噜社诞生于一个简单的想法：娱乐资讯不该只是标题和流量，它也可以有观点、有脉络、有值得停留的细节。
            </p>
            <p>
              我们把音乐、影视、时尚与潮流文化放进同一个视野里，因为它们本就彼此交织——一首 OST 会撬动一场巡演，一次街头潮流会进入时装周的秀场。
            </p>
            <p>
              于是我们选择用分类清晰的结构、克制的排版与持续的更新，为你节省筛选信息的时间，把注意力留给真正重要的内容。
            </p>
          </div>
          <div className="mt-6 grid grid-cols-3 gap-4">
            <div className="rounded-lg bg-background-50 p-4">
              <p className="text-xl font-extrabold text-primary-500 md:text-2xl">4</p>
              <p className="mt-1 text-xs text-foreground-500">内容板块</p>
            </div>
            <div className="rounded-lg bg-background-50 p-4">
              <p className="text-xl font-extrabold text-primary-500 md:text-2xl">14+</p>
              <p className="mt-1 text-xs text-foreground-500">精选资讯</p>
            </div>
            <div className="rounded-lg bg-background-50 p-4">
              <p className="text-xl font-extrabold text-primary-500 md:text-2xl">每日</p>
              <p className="mt-1 text-xs text-foreground-500">持续更新</p>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          {milestones.map((m, i) => (
            <div key={m.title} className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
              <div className="flex flex-col items-center">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-500 text-sm font-bold text-background-50">
                  {i + 1}
                </span>
                {i < milestones.length - 1 && <span className="mt-2 w-px flex-1 bg-background-300" />}
              </div>
              <div className="pb-1">
                <p className="text-xs font-semibold uppercase tracking-wide text-accent-700">{m.year}</p>
                <h3 className="mt-1 text-base font-bold text-foreground-950">{m.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{m.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 服务范围 */}
      <section className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-6">
        <div className="mb-6 flex items-center gap-2">
          <span className="h-5 w-1 rounded-full bg-primary-500" />
          <h2 className="text-xl font-bold text-foreground-950 md:text-2xl">我们提供什么</h2>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {services.map((s) => (
            <div key={s.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-2xl text-primary-700">
                <i className={s.icon} />
              </span>
              <h3 className="mt-4 text-base font-bold text-foreground-950">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 联系方式 + 反馈 */}
      <section className="mx-auto grid w-full max-w-[1280px] grid-cols-1 gap-8 px-4 pb-8 md:px-6 lg:grid-cols-[360px_1fr]">
        <div className="flex flex-col gap-4">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h3 className="text-base font-bold text-foreground-950">联系我们</h3>
            <ul className="mt-4 flex flex-col gap-3 text-sm text-foreground-600">
              <li className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-100 text-lg text-primary-700">
                  <i className="ri-mail-line" />
                </span>
                <a href="mailto:hello@lulushe.com" className="transition-colors hover:text-primary-600">
                  hello@lulushe.com
                </a>
              </li>
              <li className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-100 text-lg text-primary-700">
                  <i className="ri-phone-line" />
                </span>
                <span>400-889-2026</span>
              </li>
              <li className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-100 text-lg text-primary-700">
                  <i className="ri-map-pin-line" />
                </span>
                <span>上海市静安区创意园区 A 座</span>
              </li>
            </ul>
            <div className="mt-5 border-t border-background-200 pt-4">
              <p className="text-xs font-medium text-foreground-400">社交媒体</p>
              <div className="mt-3 flex items-center gap-3 text-xl text-foreground-500">
                <a href="https://weibo.com" target="_blank" rel="noreferrer" aria-label="微博" className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md bg-secondary-100 transition-colors hover:bg-primary-100 hover:text-primary-700">
                  <i className="ri-weibo-fill" />
                </a>
                <a href="https://www.douyin.com" target="_blank" rel="noreferrer" aria-label="抖音" className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md bg-secondary-100 transition-colors hover:bg-primary-100 hover:text-primary-700">
                  <i className="ri-tiktok-fill" />
                </a>
                <a href="https://www.xiaohongshu.com" target="_blank" rel="noreferrer" aria-label="小红书" className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md bg-secondary-100 transition-colors hover:bg-primary-100 hover:text-primary-700">
                  <i className="ri-book-marked-fill" />
                </a>
                <a href="https://www.bilibili.com" target="_blank" rel="noreferrer" aria-label="哔哩哔哩" className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md bg-secondary-100 transition-colors hover:bg-primary-100 hover:text-primary-700">
                  <i className="ri-play-circle-fill" />
                </a>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h3 className="text-base font-bold text-foreground-950">快速入口</h3>
            <div className="mt-4 flex flex-wrap gap-2">
              {categories.map((c) => (
                <Link
                  key={c.slug}
                  to={`/category/${c.slug}`}
                  className="rounded-full bg-secondary-100 px-3 py-1.5 text-xs text-secondary-900 transition-colors hover:bg-primary-100 hover:text-primary-800"
                >
                  {c.name}资讯
                </Link>
              ))}
            </div>
          </div>
        </div>

        <FeedbackForm />
      </section>
    </>
  );
}